import { z } from 'zod';

export const CONFIDENTIALITES = ['ordinaire', 'restreint', 'confidentiel'] as const;
export const schemaConfidentialite = z.enum(CONFIDENTIALITES);
export type Confidentialite = (typeof CONFIDENTIALITES)[number];

/** Types MIME admis au dépôt (détection par nombres magiques). */
export const TYPES_MIME_ADMIS = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.text',
] as const;

/** Taille maximale par défaut d'un fichier déposé (H3-07), modifiable par le paramètre depot_documents. */
export const TAILLE_MAX_DEFAUT_MO = 10;

const codeType = z.string().regex(/^[a-z][a-z0-9_]{1,62}$/, 'Code en minuscules, chiffres et soulignés');
const uuid = z.string().uuid('Identifiant invalide');

/** Champs texte du formulaire multipart de POST /documents (le fichier est dans le champ « fichier »). */
export const schemaDepotDocument = z.object({
  titre: z.string().trim().min(1).max(300),
  type_document: codeType.default('document_divers'),
  objet_type: z.enum(['courrier', 'dossier']).optional(),
  objet_id: uuid.optional(),
  confidentialite: schemaConfidentialite.default('ordinaire'),
  classement_id: uuid.optional(),
});

export const schemaNouvelleVersion = z.object({
  commentaire: z.string().trim().max(500).optional(),
});

export const schemaFiltreDocuments = z.object({
  objet_type: z.enum(['courrier', 'dossier']).optional(),
  objet_id: uuid.optional(),
  q: z.string().trim().min(1).max(200).optional(),
  limite: z.coerce.number().int().min(1).max(200).default(50),
});

export const schemaClassementDocument = z.object({
  classement_id: uuid,
});

export const schemaArchivage = z.object({
  motif: z.string().trim().min(3).max(500).optional(),
});

export const schemaCreationClassement = z.object({
  code: z.string().regex(/^[a-z0-9][a-z0-9_.-]{0,62}$/),
  libelle: z.string().trim().min(1).max(200),
  parent_id: uuid.optional(),
});

export const SORTS_FINAUX = ['conservation', 'elimination', 'a_definir'] as const;

export const schemaRegleConservation = z.object({
  type_document: codeType,
  duree_annees: z.number().int().min(0).max(200).nullable(),
  sort_final: z.enum(SORTS_FINAUX),
  commentaire: z.string().trim().max(1000).optional(),
});

export const schemaEcritureReglesConservation = z.object({
  regles: z.array(schemaRegleConservation).min(1).max(100),
  motif: z.string().trim().min(3).max(500),
});

export const schemaPropositionElimination = z.object({
  documents: z.array(uuid).min(1).max(500),
  motif: z.string().trim().min(3).max(1000),
});

export const schemaDecisionElimination = z.object({
  decision: z.enum(['valider', 'refuser']),
  motif: z.string().trim().min(3).max(1000).optional(),
});

export const schemaModeleDocument = z.object({
  code: codeType,
  libelle: z.string().trim().min(1).max(200),
  type_document: codeType.default('document_genere'),
  contenu_html: z.string().min(1).max(200_000),
  publie: z.boolean().default(true),
  motif: z.string().trim().min(3).max(500),
});

export const schemaModificationModele = schemaModeleDocument.omit({ code: true });

export const schemaApercuModele = z.object({
  variables: z.record(z.string(), z.unknown()).default({}),
});

// Réponses ---------------------------------------------------------------------------------

export const schemaDocument = z.object({
  id: uuid,
  titre: z.string(),
  type_document: z.string(),
  type_mime: z.string(),
  extension: z.string(),
  taille: z.number(),
  empreinte_sha256: z.string(),
  version_courante: z.number(),
  origine: z.enum(['depot', 'generation']),
  objet_type: z.string().nullable(),
  objet_id: z.string().nullable(),
  confidentialite: schemaConfidentialite,
  classement_id: z.string().nullable(),
  regle_conservation_id: z.string().nullable(),
  code_verification: z.string().nullable(),
  modele: z.object({ id: z.string(), version: z.number() }).nullable(),
  archive_le: z.string().nullable(),
  elimine_le: z.string().nullable(),
  cree_le: z.string(),
});
export type DocumentReponse = z.infer<typeof schemaDocument>;

export const schemaVersionDocument = z.object({
  numero: z.number(),
  empreinte_sha256: z.string(),
  taille: z.number(),
  type_mime: z.string(),
  horodatage: z.string(),
  auteur_compte_id: z.string().nullable(),
  commentaire: z.string().nullable(),
});

export const schemaFicheDocument = schemaDocument.extend({ versions: z.array(schemaVersionDocument) });

export const schemaListeDocuments = z.object({ elements: z.array(schemaDocument) });

export const schemaUrlSignee = z.object({ url: z.string(), expire_le: z.string() });

/**
 * Réponse de la vérification publique : uniquement type, organisation, date et état, et, pour un
 * document délivré au titre d'une démarche (lot L1-A), le libellé de la démarche. Jamais de donnée
 * du demandeur ni le numéro du dossier.
 */
export const schemaVerificationPublique = z.object({
  type_document: z.string(),
  organisation: z.string(),
  date_emission: z.string(),
  etat: z.enum(['valide', 'annule', 'elimine']),
  demarche: z.string().nullable(),
  /** Quittance (lot L2-A, H4-08) : numéro, rubrique, montant, état ; aucune donnée personnelle. */
  quittance: z
    .object({ numero: z.string(), rubrique: z.string(), montant: z.number(), etat: z.enum(['valide', 'annulee']), motif_annulation: z.string().nullable() })
    .optional(),
});
export type VerificationPublique = z.infer<typeof schemaVerificationPublique>;
